import { useEffect, useState } from "react";
import { api } from "../api";
import { FS } from "../lib/tokens";

// P21-zero — SETTING A PASSWORD, the missing half of a contract that already
// existed.
//
// PUT /api/collaborators/:id has accepted a `password` since requireSelfOrAdmin
// was written, and hashed it with the login path's own bcrypt cost — and no
// screen in the app ever sent one. So a user could not change his own password
// and an admin could not reset anybody's: the only way to set one at all was
// scripts/create-admin.js, at creation, by an operator with a shell. That is
// what this closes.
//
// ONE COMPONENT FOR BOTH FLOWS, because the server has one rule for both. The
// self-service case and the admin-resets-somebody-else case are the SAME
// request to the SAME route under the SAME gate (self-or-admin); rendering two
// components would be two clients for one contract, and they would drift. The
// caller decides only whether to render it at all.
//
// NO OLD PASSWORD, and that is the server's decision showing through rather
// than a shortcut. Authority is established by requireSelfOrAdmin — you are
// this row, or you are an admin — and asking for the current password would
// make the admin half impossible, since an admin does not know it.
//
// STDIN-CLASS CARE, as far as a browser allows:
//   · the plaintext lives ONLY in this component's state and dies with it. It
//     is never merged into the modal's `form`, so no ordinary save carries it
//     and no form-shaped log or localStorage mirror can pick it up;
//   · it is never passed to console/toast — a failure reports the REASON, not
//     the value;
//   · type="password" and autoComplete="new-password" on both inputs, so a
//     password manager offers to store rather than to autofill, and no browser
//     treats this as a login form;
//   · it is cleared on success, so it does not sit in a detached DOM node.
//
// The length floor is the SERVER's (lib/password.js, the same twelve
// characters scripts/create-admin.js has always enforced) — checked here only
// so the user is told before the round trip, never INSTEAD of the round trip.
//
// 04g §2 — A CREATE MODE, which owns no request. On create there is no row to
// PUT to, and the password has to ride the modal's own POST — so `mode="create"`
// keeps every RULE here (the twelve-character floor, the confirm entry, the
// hint, type="password" + autoComplete="new-password") and drops only the
// transport: no button, no api call, no toast. It reports upward through
// `onValue`, which receives the validated plaintext or "" and nothing in
// between, so the caller can never hold a half-typed or mismatched value.
//
// WHY NOT A SECOND INPUT NEXT TO THIS ONE: the floor and the confirm-entry
// semantics are the part that drifts, and lib/password.js exists precisely
// because three writers once disagreed about them while agreeing on the hash.
// A separate create input would be a fourth. The header's "one component for
// both flows" argument was about self-service vs admin-reset being one ROUTE;
// this is a different route with the same RULES, which is the half worth
// sharing.
//
// The lift through `onValue` is the one place the plaintext leaves this
// component, and it is deliberate — see App.tsx's coCreatePwRef, which is a
// ref rather than state so the value never enters `form`, the sync engine or
// the store.
const MIN_LEN = 12;

export const PasswordField = ({
  collaboratorId,
  isSelf,
  t,
  toast,
  mode = "put",
  onValue,
  required = false,
}: any) => {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const isCreate = mode === "create";

  const tooShort = pw.length > 0 && pw.length < MIN_LEN;
  const mismatch = pw2.length > 0 && pw !== pw2;
  const ready = pw.length >= MIN_LEN && pw === pw2 && !busy;

  // Create mode reports only a value the server would accept; anything else
  // is "" so a partially-typed entry can never be sent.
  useEffect(() => {
    if (isCreate && typeof onValue === "function") {
      onValue(pw.length >= MIN_LEN && pw === pw2 ? pw : "");
    }
  }, [isCreate, onValue, pw, pw2]);

  const submit = async () => {
    if (!ready) return;
    setBusy(true);
    try {
      // Only the password. A full-object save would re-send every field the
      // modal is holding, and the server's non-admin field matrix would then
      // have to forgive a form the user did not touch — the C74b problem,
      // solved by not creating it.
      await api.put(`/collaborators/${collaboratorId}`, { password: pw });
      setPw("");
      setPw2("");
      toast(
        isSelf
          ? t("collab.password.set_self", {
              fallback: "Your password has been changed.",
            })
          : t("collab.password.set_other", {
              fallback: "Password set for this collaborator.",
            }),
        "success",
      );
    } catch (e: any) {
      // The REASON, never the value.
      toast(
        (e && e.message) ||
          t("collab.password.failed", { fallback: "Could not set the password." }),
        "error",
      );
    } finally {
      setBusy(false);
    }
  };

  const hint = tooShort
    ? t("collab.password.too_short", {
        fallback: `At least ${MIN_LEN} characters.`,
      })
    : mismatch
      ? t("collab.password.mismatch", { fallback: "The two entries differ." })
      : isCreate
        ? required
          ? t("collab.password.hint_create_required", {
              fallback: `At least ${MIN_LEN} characters. Microsoft sign-in is not configured on this server, so without a password this person cannot sign in.`,
            })
          : t("collab.password.hint_create_optional", {
              fallback: `At least ${MIN_LEN} characters. Optional — leave blank if this person signs in with Microsoft.`,
            })
        : t("collab.password.hint", {
            fallback: `At least ${MIN_LEN} characters. Takes effect at the next sign-in.`,
          });

  return (
    <>
      <div className="field-row">
        <div className="field">
          <label>
            {isCreate
              ? required
                ? t("collab.password.new_create_required", {
                    fallback: "Password *",
                  })
                : t("collab.password.new_create", { fallback: "Password" })
              : isSelf
                ? t("collab.password.new_self", { fallback: "New password" })
                : t("collab.password.new_other", {
                    fallback: "Set a new password",
                  })}
          </label>
          <input
            className="field-input"
            type="password"
            autoComplete="new-password"
            name="lx-new-password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
          />
        </div>
        <div className="field">
          <label>{t("collab.password.confirm", { fallback: "Confirm" })}</label>
          <input
            className="field-input"
            type="password"
            autoComplete="new-password"
            name="lx-new-password-2"
            value={pw2}
            onChange={(e) => setPw2(e.target.value)}
          />
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <div
            style={{
              fontSize: FS.s9_5,
              color: tooShort || mismatch ? "var(--rd)" : "var(--tm)",
              lineHeight: 1.4,
              paddingTop: 5,
            }}
          >
            {hint}
          </div>
        </div>
        {/* 04g §2 — no button in create mode: the value rides the modal's own
            POST, so a second request here would be a second write with nothing
            to write to. The field is otherwise identical. */}
        <div className="field">
          {isCreate ? null : (
          <button
            type="button"
            className={`btn ${ready ? "btn-primary" : "btn-ghost"}`}
            disabled={!ready}
            style={{ padding: "4px 12px", fontSize: FS.s10, opacity: ready ? 1 : 0.45 }}
            onClick={submit}
          >
            {busy
              ? t("common.saving", { fallback: "Saving…" })
              : t("collab.password.apply", { fallback: "Set password" })}
          </button>
          )}
        </div>
      </div>
    </>
  );
};
